import type { Screen0LanguageCode } from "@/features/language/language-registry";

export const EN_LANGUAGE_CODE: Screen0LanguageCode = "en";

export const EN_COMMON_COPY = {
  appName: "Screen0",
  loading: "Loading…",
  loadingSettings: "Loading settings",
  loadingInsight: "Loading insight…",
  close: "Close",
  cancel: "Cancel",
  save: "Save",
  delete: "Delete",
  done: "Done",
  send: "Send",
  add: "Add",
  create: "Create",
  edit: "Edit",
  available: "Available",
  unavailable: "Unavailable",
  checking: "checking",
  somethingWentWrong: "Something went wrong",
  today: "Today",
  thisWeek: "This week",
  thisMonth: "This month",
  backToToday: "Back to Today",
  openSettings: "Open settings",
  primaryNavLabel: "Primary",
  noUsage: "No usage",
  emDash: "—",
  thinking: "Thinking...",
  opening: "Opening…",
  ok: "OK",
  dismiss: "Dismiss",
  unknown: "Unknown",
} as const;

export const EN_NAVIGATION_COPY = {
  today: "Today",
  habits: "Habits",
  insight: "Insight",
  rules: "Rules",
  settings: "Settings",
  premiumLocked: "Premium",
} as const;

export const EN_TODAY_COPY = {
  titleToday: "Today",
  titleThisWeek: "This week",
  titleThisMonth: "This month",
  periodDay: "Day",
  periodWeek: "Week",
  periodMonth: "Month",
  periodLabel: "Period",
  viewApps: "Apps",
  viewCategories: "Categories",
  metricScreenTime: "Screen Time",
  metricScreenTimeAndroid: "Usage time",
  metricActivations: "Activations",
  viewToggleAria: "View: {view}. Tap to switch.",
  metricToggleAria: "Metric: {metric}. Tap to switch.",
  periodToggleAria: "Period: {period}. Tap to switch.",
  prevNavAria: "Previous {period}",
  nextNavAria: "Next {period}",
  navDay: "day",
  navWeek: "week",
  navMonth: "month",
  noSnapshotDayTitle: "No snapshot for this day",
  noSnapshotWeekTitle: "No snapshots for this week",
  noSnapshotMonthTitle: "No snapshots for this month",
  noSnapshotDayBody:
    "No snapshot was saved for this day. Try another date or return to today.",
  noSnapshotWeekBody:
    "This week does not have enough saved snapshots yet. Try an earlier week or check back after more daily captures.",
  noSnapshotMonthBody:
    "This month does not have enough saved snapshots yet. Try an earlier month or check back after more daily captures.",
  preInstallDayTitle: "No data for this day",
  preInstallDayBody:
    "Usage data is only available from when Screen0 was first installed on this device. Dates before then aren't available.",
  preInstallWeekTitle: "No data for this week",
  preInstallWeekBody:
    "This entire week falls before Screen0 was installed on this device, so usage data isn't available.",
  preInstallMonthTitle: "No data for this month",
  preInstallMonthBody:
    "This entire month falls before Screen0 was installed on this device, so usage data isn't available.",
  noUsageRecorded: "No usage recorded yet",
  permissionNeededTitle: "Permission needed for today's report",
  permissionNeededBody:
    "Screen0 works without an account, but iOS requires Screen Time permission before it can show app usage.",
  permissionNeededBodyAndroid:
    "Screen0 needs Usage access to show today's app usage. Tap below to open system Settings, find Screen0, and turn Usage access on. Android does not show a pop-up dialog for this permission.",
  permissionNeededBodyAndroidRestricted:
    "Android blocked this permission because Screen0 was installed outside an app store. First tap “Open Screen0 app info” below, open the ⋮ menu in the top-right corner, and choose “Allow restricted settings” (confirm with your PIN). Then come back and turn Usage access on.",
  openAppInfo: "Open Screen0 app info",
  permissionDeniedHint:
    "Open Settings and tap Request Screen Time access, then approve Apple's “App & Website Usage” dialog to unlock today's report.",
  openUsageAccess: "Open Usage access settings",
  reasonIosDataExportRequires264:
    "Apple only lets apps read Screen Time data on iOS 26.4 or later. Update iOS to see usage here.",
  reasonDataExportUnavailable:
    "Apple currently allows Screen Time data export only on devices in the EU, so no usage can be read on this device.",
  reasonExtensionSnapshotMissing:
    "The report extension started, but the app-group snapshot was not readable in this run.",
  reasonReportViewNotMounted:
    "Screen0 could not mount the native Device Activity report host.",
  reasonDefaultNoData:
    "Use your phone for a few minutes, then switch back to Today or reopen Screen0. iOS can also return no data early in the day.",
  reasonDefaultNoDataAndroid:
    "Usage access is on. Use the device for a few minutes, then return to Today or reopen Screen0 — Android can take a moment to report the first data.",
  collectingUsageTitle: "Reading usage data…",
  collectingUsageBody:
    "Usage access is on. Screen0 is reading today's usage now — this takes just a moment.",
  collectingUsageBodyAndroid:
    "Usage access is on. Screen0 is reading today's usage from Android — right after granting access this can take a few seconds.",
  reasonUsageEventsUnavailableAndroid:
    "Usage access looks on, but Android is not sharing usage data with Screen0 yet. Open Settings, turn Usage access off and on again for Screen0, then reopen the app. On installs from outside an app store, first choose “Allow restricted settings” from App info (⋮ menu).",
  reasonAppsNotVisibleAndroid:
    "Android reported usage, but Screen0 could not identify the apps involved. Update or reinstall Screen0, then try again.",
  weeklyActivationsNotTracked: "Weekly activations are not tracked yet",
  monthlyActivationsNotTracked: "Monthly activations are not tracked yet",
  activationsNotTracked: "Activations not tracked yet",
  topApps: "Top apps",
  allApps: "All apps",
  topCategories: "Top categories",
  allCategories: "All categories",
  topActivations: "Top activations",
  allActivations: "All activations",
  topCategoryActivations: "Top category activations",
  allCategoryActivations: "All category activations",
  viewAllAppsAria: "View all apps",
  viewAllCategoriesAria: "View all categories",
  viewAllActivationsAria: "View all activations",
  viewAllCategoryActivationsAria: "View all category activations",
  dayShape: "Day shape",
  weekShape: "Week shape",
  monthShape: "Month shape",
  viewMore: "View more",
  dailyAverage: "Daily avg",
  dailyAverageOn: "on",
  dailyAverageOff: "off",
  dailyAverageToggleAria: "Daily average {state}. Tap to toggle.",
  reasonActivityDataFetchFailed:
    "iOS could not return Screen Time data this time. Use your phone for a few minutes, then switch back to Today or reopen Screen0.",
  reasonReportExtensionNotInvoked:
    "iOS did not run the Device Activity report extension while Screen0 was collecting.",
  dayAriaSelectedToday: "{label}, selected, today",
  dayAriaSelected: "{label}, selected",
  dayAriaToday: "{label}, today",
} as const;

export const EN_HABITS_COPY = {
  title: "Habits",
  timeframe: "Last 12 months",
  reorder: "Reorder apps",
  doneReordering: "Done reordering",
  openFilter: "Open habits filter",
  metricToggleAria: "Metric: {metric}. Tap to switch.",
  emptyTitle: "No app usage data yet",
  emptyBody:
    "Grant Screen Time access from Today to start tracking habits and patterns.",
  emptyBodyAndroid:
    "Grant Usage access from Today to start tracking habits and patterns.",
  filterView: "View",
  filterTrackApps: "Track apps",
  filterTrackCategories: "Track categories",
  filterCategories: "Categories",
  filterShowingAll: "Showing all. Select to track only specific ones.",
  filterTrackingSelected: "Tracking {count} selected.",
  filterSearchPlaceholder: "Search apps you've used",
  filterSearchAria: "Search apps",
  closeFilter: "Close filter",
  previousMonth: "Previous month",
  nextMonth: "Next month",
  filterTitle: "Filter",
  filterSearchResults: "Search results",
  filterSearchHistoryApps: "Other apps from history",
  filterSearching: "Searching…",
  filterNoMatchingApps: "No matching apps found.",
  filterNoItemsToTrack: "No items to track yet.",
  filterShowAll: "Show all",
  filterTrackLimitNear: "You can track up to {cap} apps. {slots} slots left.",
  filterTrackLimitReached: "Tracking limit reached ({cap}). Uncheck an app to add another.",
  filterOtherSection: "Other bucket",
  includeOther: "Include Other",
  includeOtherAria: "Include Other bucket in habits",
  includeOtherInfoAria: "What is the Other bucket?",
  otherInfoTitle: "What is Other?",
  otherInfoBody:
    "Other is screen time iOS couldn't assign to a specific app. It often comes from uninstalled apps, system usage, or small gaps between your total Screen Time and the sum of per-app usage.",
  otherInfoBodyAndroid:
    "Other is usage time Android couldn't assign to a specific app. It often comes from uninstalled apps, system usage, or small gaps between your total usage and the sum of per-app usage.",
} as const;

export const EN_INSIGHT_COPY = {
  title: "Insight",
  timeframe: "Last {days} days",
  compulsivity: "Compulsivity",
  doomScrollRisk: "Doom-scroll",
  aboutMetricAria: "About {metric}",
  compulsivityBody1:
    "How often you reopen the same apps relative to everything else on your phone. High scores usually mean frequent pickups and repeat visits, not just long sessions.",
  compulsivityBody2:
    "Screen0 blends how often an app is opened, how much total time it takes, and how many days it shows up in your recent snapshots.",
  compulsivityBody3:
    "These are proxy estimates from the last 14 days — useful patterns, not a clinical measure.",
  doomBody1:
    "Apps where usage tends to repeat and cluster into a few hours of the day — the kind of pattern that can stretch into mindless scrolling, at any time.",
  doomBody2:
    "Screen0 weighs how much time an app takes, how consistently it appears, and how concentrated that usage is across the day in your recent snapshots.",
  doomBody3:
    "Scores are proxy estimates from the last 14 days and reflect habits, not intent.",
  activateSmartBlockAria: "Activate Smart Block for {appName}",
  reactivateSmartBlockAria: "Reactivate Smart Block for {appName}",
  activateSmartBlockTitle: "Activate this Smart Block?",
  reactivateSmartBlockTitle: "Reactivate this Smart Block?",
  activateConfirmBody:
    "Creates an active rule that suspends {appName} every day, {window}.",
  reactivateConfirmBody:
    "Turns your existing rule back on for {appName} every day, {window}.",
  activate: "Activate",
  reactivate: "Reactivate",
  notFoundTitle: "Insight not found",
  notFoundBody: "This insight may have been deleted or has not been generated yet.",
  statusPermissionNeededTitle: "Permission needed",
  statusPermissionNeededBody:
    "Allow Screen Time access from Today before Insight can score recent app patterns.",
  statusPermissionNeededBodyAndroid:
    "Allow Usage access from Today before Insight can score recent app patterns.",
  statusNoUsageTitle: "No usage data yet",
  statusNoUsageBody:
    "Open Today after using your phone so Screen0 can save snapshots for this tab.",
  statusEarlyReadTitle: "Early read",
  statusEarlyReadBody:
    "Only {days} {dayWord} of data in this {windowLabel} window. Scores are conservative until more snapshots are saved.",
  daySingular: "day",
  dayPlural: "days",
  statusLimitedOpensTitle: "Limited opens signal",
  statusLimitedOpensBody:
    "These scores use time and recurrence, but no opens proxy was available in the recent snapshots.",
  smartBlockSuggestionsTitle: "Smart Block suggestions",
  smartBlockSuggestionsBody: "Tap a suggestion to turn it into an active rule on this device.",
  activeSmartBlocksTitle: "Active Smart Blocks",
  activeSmartBlocksAria: "Active Smart Block rules on this device",
  goToActiveSmartBlockRuleAria: "Go to rule for {appName}",
  smartBlockEmptyTitle: "No conservative block yet",
  smartBlockEmptyBody:
    "Screen0 needs stronger repeated usage signals before suggesting blocks.",
  metricCircleAria: "{appName}, {kindLabel} {score} percent. Tap for details.",
  metricDetailsAria: "{appName} details",
  metricActivationsCount: "{count} activations",
  smartBlockActiveAgain: "{appName} Smart Block is active again.",
  smartBlockOn: "{appName} Smart Block is on.",
  smartBlockAlreadyBlocked: "{appName} is already blocked in this window.",
  kindCompulsivityLabel: "compulsivity",
  kindDoomLabel: "doom-scroll",
  tryToday: "Try today",
  backLink: "Insight",
  smartBlockBadge: "Smart Block",
  openExclude: "Manage apps excluded from Insight",
  excludeSheetTitle: "Exclude from Insight",
  closeExclude: "Close exclusions",
  excludeSectionLabel: "Excluded apps",
  excludeHelperAllIncluded:
    "All apps are included in Insight scores. Check apps to exclude them from compulsivity, doom-scroll, and Smart Block suggestions.",
  excludeHelperCount: "{count} apps excluded from Insight scores.",
  excludeSearchPlaceholder: "Search apps you've used",
  excludeSearchAria: "Search apps",
  excludeSearchResults: "Search results",
  excludeSearchHistoryApps: "Other apps from history",
  excludeSearching: "Searching…",
  excludeNoMatchingApps: "No matching apps",
  excludeNoApps: "No apps in this window yet",
  excludeIncludeAll: "Include all",
  smartBlockPrePickerTitle: "Choose apps in Screen Time",
  smartBlockPrePickerReactivateTitle: "Re-select apps in Screen Time",
  smartBlockPrePickerBody:
    "The next screen is Apple's app picker. Choose {appName} so Screen0 can block it during this Smart Block window.",
  smartBlockPrePickerReactivateBody:
    "The next screen is Apple's app picker. Choose {appName} again so Screen0 can enforce this Smart Block.",
  smartBlockPrePickerNext: "Next",
  smartBlockPickerTitle: "Choose {appName}",
  smartBlockPickerHeader: "Choose {appName}",
  smartBlockPickerFooter:
    "Select the app suggested by your Smart Block. Screen0 cannot verify your choice — pick carefully.",
  smartBlockHonorTitle: "Did you choose {appName}?",
  smartBlockHonorBody:
    "On the previous screen, you should have selected the app this Smart Block targets.",
  smartBlockHonorYes: "Yes, continue",
  smartBlockHonorNo: "No",
  smartBlockDeclinedTitle: "Smart Block needs {appName}",
  smartBlockDeclinedBody:
    "This Smart Block cannot be created unless you choose {appName} in Apple's picker.",
  smartBlockRetryPicker: "Try again",
  smartBlockNonConformingTitle: "Selection doesn't match Smart Block",
  smartBlockNonConformingBody:
    "You selected {appCount} apps, {categoryCount} categories, and {siteCount} websites. The rule can still be created, but it will not be tagged as a Smart Block.",
  smartBlockNonConformingContinue: "Create manual rule",
} as const;

export const EN_RULES_COPY = {
  title: "Rules",
  reorder: "Reorder",
  doneReordering: "Done reordering",
  dismiss: "Dismiss",
  activeRulesTitle: "Scheduled Rules",
  activeRulesLoading: "Loading…",
  activeRulesEnforcing: "{count} enforcing now",
  activeRulesEmptySchedule: "Schedule a suspension",
  noActiveRulesTitle: "No scheduled rules",
  noActiveRulesBody:
    "Choose what to block, when it applies, and which days — your first rule takes about a minute.",
  newRule: "New rule",
  targetsTitle: "Targets",
  targetsSaved: "{count} saved",
  targetsEmptySubtitle: "Reusable blocking bundles for apps & sites",
  noTargetsTitle: "No targets yet",
  noTargetsBody:
    "Create a named bundle of apps, sites, or categories — then reuse it in any rule or quick session.",
  newTarget: "New target",
  addSectionAria: "Add {title}",
  deleteRuleAria: "Delete rule",
  deleteTargetAria: "Delete target",
  deleteRuleConfirmAria: "Delete this rule?",
  deleteTargetConfirmAria: "Delete this target?",
  deleteRuleConfirm: "Delete this rule?",
  deleteTargetConfirm: "Delete this target?",
  deleteRuleCoveredConfirm:
    "Delete this rule? The same apps and sites stay blocked by another active rule.",
  deleteTargetCoveredConfirm:
    "Delete this target? The same apps and sites stay blocked by another active rule.",
  deleteRuleCoveredConfirmAria: "Delete this rule while blocking continues?",
  deleteTargetCoveredConfirmAria: "Delete this target while blocking continues?",
  editRuleAria: "Edit {name}",
  editTargetAria: "Edit {targetName}",
  enableRuleAria: "Enable rule",
  disableRuleAria: "Disable rule",
  noTargets: "No targets",
  ruleUpdated: "Rule updated.",
  ruleCreated: "Rule created.",
  ruleSaveFailed: "Couldn't save rule. Try again.",
  targetCreated: "Target created.",
  targetUpdated: "Target updated.",
  targetSaveFailed: "Couldn't save target. Try again.",
  ruleDeleted: "Rule deleted.",
  targetDeleted: "Target deleted. Rules using only this target were removed.",
  editRule: "Edit rule",
  newRuleEditor: "New rule",
  saveRule: "Save rule",
  createRule: "Create rule",
  editTarget: "Edit target",
  newTargetEditor: "New target",
  saveTarget: "Save target",
  createTarget: "Create target",
  targetsSection: "Targets",
  targetsLimitHint: "Up to {max} per rule",
  targetsLimitReached: "You can select up to {max} targets per rule. Deselect one to add another.",
  targetsRequired: "Select at least one target to save this rule.",
  targetEditorFromRule: "Adding a target for this rule",
  targetEditorFromQuickStart: "Adding a target for quick start",
  backToRule: "Back to rule",
  backToQuickStart: "Back to quick start",
  websiteCategoriesLink: "Browse website categories",
  websiteCategoriesHint: "Block whole groups like Social or News — no need to type domains.",
  createTargetCta: "Create your first target",
  createTargetHint: "Name a bundle of apps or sites, then pick it in rules.",
  daysSection: "Days",
  timeWindowSection: "Time window",
  startLabel: "Start",
  endLabel: "End",
  endsLabel: "Ends",
  durationMode: "Duration",
  endTimeMode: "End time",
  blockDurationAria: "Block duration in minutes",
  targetNamePlaceholder: "e.g. Social, Work distractions",
  chooseAppsSites: "Choose apps & sites to block",
  websitePlaceholder: "example.com",
  websiteAria: "Website domain",
  tapToChangeBlocked: "Tap to change what's blocked",
  chosenOnDevice: "Chosen on this iPhone",
  changeBlockedAppsAria: "Change blocked apps",
  viewFullBlockedListAria: "View full blocked list",
  deleteKindAria: "Delete {kind}",
  windowNoMidnightNote: "Windows stay within one day — they never cross midnight.",
  targetNameRequired: "This field is required",
  targetAppsCategoriesSection: "Apps, categories",
  targetOpenOnIphone:
    "Open Screen0 on your iPhone to choose the apps and sites this target blocks.",
  targetContentRequired: "Choose apps & sites to block, or add a website below.",
  targetNameLabel: "Name",
  targetWebsitesSection: "Websites",
  targetSitesFromPicker: "{count} {siteWord} from Screen Time picker",
  siteSingular: "site",
  sitePlural: "sites",
  pickerSitesCount: "{count} {siteWord}",
  view: "View",
  smartBlockBadge: "Smart Block",
  smartBlockRuleWindowTitle: "Edit Smart Block time",
  smartBlockRuleWindowBody:
    "You can only change when this Smart Block runs. Apps, days, and other settings stay locked.",
  smartBlockRuleWindowAria: "Edit Smart Block time range",
  smartBlockMinDurationError: "Smart Blocks must be at least 1 hour long.",
  smartBlockEndBeforeStartError: "End time must be after start time.",
  smartBlockRuleNotEditable: "You can't edit a Smart Block rule.",
  smartBlockTargetNotEditable: "Smart Block targets can't be edited.",
  smartBlockRuleNotEditableAria: "Smart Block rule not editable",
  smartBlockTargetNotEditableAria: "Smart Block target not editable",
  enforcementBannerTitle: "Enforcing now",
  enforcementBannerEndsIn: "Ends in {time}",
  enforcementBannerMore: "+{count} more",
  enforcementBreakBannerTitle: "Break active",
  enforcementBreakBannerEndsIn: "Ends in {time}",
  quickStart: {
    fabLabel: "Start",
    fabAria: "Start a quick blocking session",
    drawerTitle: "Quick start",
    durationLabel: "Duration",
    durationAria: "Quick session duration in minutes",
    recentTab: "Recent",
    savedTab: "Saved",
    savePrompt: "Name this session",
    savePromptTitle: "Save to favorites",
    savePromptHint: "Star a recent session to save it here for one-tap reuse.",
    saveNamePlaceholder: "e.g. Focus hour",
    noRecents: "No recent sessions yet",
    noSaved: "No saved sessions yet",
    createTarget: "New target",
    changeIcon: "Change icon",
    sessionActive: "Blocking now",
    saveStarAria: "Save session",
    savedStarAria: "Session saved",
    defaultSessionName: "Session",
    startButton: "Start session",
    noValidTargets: "Those targets were removed. Select at least one target.",
  },
  swipeUnlock: {
    trackLabel: "Slide to stop blocking",
    trackAria: "Drag to stop all blocking",
    overlayTitle: "Hold to unlock",
    overlayBody: "This stops every rule and session enforcing right now. Hold to confirm you really want to unlock.",
    holdAria: "Press and hold to confirm",
    holdHint: "Keep holding…",
    takeBreak: "Take a five minute break",
  },
  forcedSuspend: {
    badge: "You paused",
    subtitle: "Tap power to resume blocking.",
    autoResume: "Resumes {time}.",
    resumeRuleAria: "Resume blocking for this rule",
  },
  vpnInstallPrompt: {
    title: "Install the website filter",
    body: "A rule is blocking websites right now. Install the on-device VPN profile so those sites stay blocked in Safari.",
    bodyAndroid:
      "A rule is blocking websites right now. Install the on-device VPN profile so those sites stay blocked in Chrome and other browsers.",
    install: "INSTALL PROFILE",
    installing: "INSTALLING…",
    notNow: "NOT NOW",
    dismissAria: "Dismiss website filter prompt",
  },
  vpnWebTargetGate: {
    title: "Website filter required",
    body:
      "Blocking websites needs the on-device filter profile. App-only targets work without it — install the profile to add typed domains or website categories.",
    banner:
      "Install the on-device website filter to add domains or categories here.",
    install: "INSTALL PROFILE",
    installing: "INSTALLING…",
    continueAppsOnly: "Apps only",
    dismissAria: "Dismiss website filter requirement",
    saveBlocked: "Install the website filter to save new domains or categories.",
  },
  appBlockingPermissionGate: {
    overlayBanner:
      "App blocking needs “Display over other apps”. Turn it on so the block screen can appear.",
    overlayCta: "OPEN SETTINGS",
    usageBanner:
      "App blocking needs Usage Access. Turn it on so Screen0 can see which app is open.",
    usageCta: "OPEN USAGE ACCESS",
    bannerAria: "Enable required permission for app blocking",
  },
} as const;

export const EN_WEBSITE_CATEGORIES_COPY = {
  modalTitle: "Website categories",
  expandInfoAria: "Show details for {name}",
  collapseInfoAria: "Hide details for {name}",
  toggleCategoryAria: "Toggle {name}",
  domainsHeading: "Included domains",
  removeCategoryAria: "Remove {name}",
  browseCategoriesButton: "Categories",
  browseCategoriesAria: "Open website categories",
  addWebsiteAria: "Add website",
  social: {
    name: "Social",
    description: "Social networks, forums, and community platforms.",
  },
  meta: {
    name: "Meta",
    description: "Facebook, Instagram, WhatsApp, and Messenger.",
  },
  messaging: {
    name: "Messaging",
    description: "Chat apps and direct messaging services.",
  },
  news: {
    name: "News",
    description: "News sites, publishers, and information outlets.",
  },
  dating: {
    name: "Dating",
    description: "Dating apps and matchmaking websites.",
  },
} as const;

export const EN_CHAT_COPY = {
  title: "Ask Screen0",
  engineIosLocal: "iOS on-device AI",
  engineAndroidLocal: "Android on-device AI",
  engineCloud: "Cloud AI",
  closeChatAria: "Close chat",
  suggestionWhatChanged: "What changed today?",
  suggestionWhichApp: "Which app pulled me in?",
  suggestionTonight: "What should I try tonight?",
  placeholder: "Ask about your usage",
  sendAria: "Send",
  unableToAnswer: "Unable to answer right now.",
  localAiUnavailableSuffix:
    "On-device AI is unavailable right now. Cloud chat is not used automatically on this device.",
} as const;

export const EN_INSIGHT_CHAT_COPY = {
  fabAria: "Ask about your phone usage",
  title: "Usage chat",
  privacyBadge: "100% on-device",
  engineIosLocal: "iOS on-device AI",
  engineAndroidLocal: "Android on-device AI",
  greeting:
    "What would you like to know about how you use your phone? I can explain patterns from your recent Insight window.",
  suggestionTotalUsage: "How much have I used my phone?",
  suggestionTopApp: "Which app do I use the most?",
  suggestionBusiestDays: "Which days do I use my phone most?",
  suggestionBusiestBands: "Which time bands are busiest?",
  suggestionTopAppWhy: "Why do I tend to use {appName} so much?",
  suggestionTopAppWhyGeneric: "Why do I tend to use my most-used app so much?",
  suggestionComparePeriods: "Compare this period vs the previous one",
  placeholder: "Ask about your usage patterns",
  sendAria: "Send message",
  closeAria: "Close usage chat",
  unableToAnswer: "Unable to answer right now.",
  contextLoading: "Preparing your usage context…",
  contextError: "Couldn't load your usage context. Try again or close and reopen chat.",
  contextEmpty: "Not enough recent usage data for chat yet.",
  retryContext: "Try again",
} as const;

export const EN_SETTINGS_COPY = {
  title: "Settings",
  controlsEyebrow: "Controls",
  screenTimeSection: "Screen Time",
  screenTimeSectionAndroid: "Usage access",
  permissionLabel: "Permission",
  requestingAccess: "Requesting access",
  requestScreenTimeAccess: "Request Screen Time access",
  /** Android: opens system Usage Access (PACKAGE_USAGE_STATS special permission). */
  openUsageAccess: "Open Usage access settings",
  openingUsageAccess: "Opening Usage access…",
  legalSection: "Legal",
  privacyPolicyRow: "Privacy Policy",
  termsRow: "Terms",
  resetSection: "Reset",
  deletingSavedData: "Deleting saved data",
  deleteSavedData: "Delete saved data and reset device ID",
  resetSuccess: "Deleted {count} saved records. A new anonymous device ID was created.",
  resetFailed: "Reset failed.",
  footerNoAccount: "No account required.",
  languageRow: "Language",
  purchasesSection: "Purchases",
} as const;

export const EN_LANGUAGE_COPY = {
  eyebrow: "App language",
  title: "Languages",
  subtitle:
    "Screen0 follows the preferred app language configured in the system. Change it from iPhone Settings whenever you want.",
  subtitleAndroid:
    "Screen0 follows the preferred app language configured in the system. Change it from Android Settings whenever you want.",
  subtitleAndroidLegacy:
    "On this Android version, Screen0 follows the device language. Change it in system Settings → Languages & input.",
  activeLanguageLabel: "Active language",
  systemLanguageLabel: "System preference",
  fallbackNote:
    "The selected system language is not yet supported by Screen0, so the app uses English as fallback.",
  unavailableSystemValue: "Not available",
  openSettingsTitle: "Open iPhone Settings",
  openSettingsTitleAndroid: "Open app language settings",
  openSettingsTitleAndroidLegacy: "Open Android Settings",
  openSettingsSubtitle:
    "Go to the Screen0 system page to change the app language.",
  openSettingsSubtitleAndroid:
    "Go to Screen0's language page to change the app language.",
  openSettingsSubtitleAndroidLegacy:
    "App info opens here. Change the device language in system Languages — per-app language needs Android 13 or newer.",
  openingSettingsLabel: "Opening Settings",
  closeLabel: "Close",
  footerReady:
    "When you return to the app, Screen0 automatically syncs the updated language.",
  footerReadyAndroid:
    "When you return to the app, Screen0 automatically syncs the updated language.",
  footerReadyAndroidLegacy:
    "When you return to the app, Screen0 syncs to the current device language.",
  footerOpening: "Opening system page...",
  openSettingsUnavailableTitle: "Settings unavailable",
  openSettingsUnavailableDescription:
    "I cannot open the app Settings from this environment.",
} as const;

export const EN_PERMISSION_COPY = {
  gateTitle: "Permission needed for today's report",
  gateBody:
    "Screen0 works without an account, but iOS requires Screen Time permission before it can show app usage.",
  gateBodyAndroid:
    "Screen0 needs Usage access to show today's app usage. Android does not show a permission pop-up — open Settings, find Screen0 in Usage access, and turn it on.",
  requestingAccess: "Requesting access",
  requestScreenTimeAccess: "Request Screen Time access",
  openUsageAccess: "Open Usage access settings",
  openingUsageAccess: "Opening Usage access…",
  entitlementMissing:
    "This build is missing the Family Controls entitlement or matching provisioning profile. Screen Time data cannot be collected until signing is fixed.",
} as const;

export const EN_ONBOARDING_COPY = {
  progressLabel: "Onboarding progress",
  continue: "Continue",
  working: "Working…",
  goToToday: "Go to Today",
  narrative4Cta: "What do you want that time for?",
  narrative1ImageAlt: "Zero with a screen in hand without noticing",
  narrative2ImageAlt: "Zero pulled by strings toward a glowing screen",
  narrative3ImageAlt: "Zero at a table without a phone",
  narrative4ImageAlt: "Zero taking a first step on a path without a phone",
  narrative1: [
    "Have you ever noticed how you end up with a screen in your hand without even realizing it? It only takes **15 seconds** of waiting and you're already reaching for the screen.",
    "We've learned to **never leave empty space** and not to contemplate even for an instant.",
    "Ping, scroll, tab: it's easy to feel confused and **divided**.",
  ],
  narrative2: [
    "It's not a lack of **strength and willpower**. Apps are designed and engineered to **keep you there**: every scroll makes it harder to **pull away**.",
    "Screen0 helps you **create distance**, not try to be **perfect**.",
  ],
  narrative3: [
    "Less screen doesn't mean less life — it means more **space** for work, people, sleep.",
    "Experience again a meal without your phone, an evening without scrolling: the boundaries seem small, but they make a **big difference**.",
  ],
  narrative4: [
    "**You've already taken the first step.**",
    "A walk without your phone. A morning without the feed. Change **starts here**.",
  ],
  benefitsHeadline: "Here is how **Screen0** can help you in the next week",
  benefits: [
    {
      title: "Focus in minutes",
      subtitle: "Easy and customizable setup",
    },
    {
      title: "Get 2+ hours back daily",
      subtitle: "Less scrolling. More time for the things that matter.",
    },
    {
      title: "Get more done",
      subtitle: "Become up to 50% more productive at work with Screen0",
    },
    {
      title: "Get better sleep",
      subtitle: "Stop late-night scrolling with a bedtime schedule that actually works",
    },
  ],
  trialHeadline: "Try Premium for **free.** No surprises.",
  trialRows: [
    {
      dayLabel: "TODAY",
      title: "No charge today",
      subtitle: "Just access to Premium features like Rules and Smart Blocks",
    },
    {
      dayLabel: "DAY 5",
      title: "Reminder before trial ends",
      subtitle: "We'll remind you 2 days before your free trial ends",
    },
    {
      dayLabel: "DAY 7",
      title: "Cancel anytime",
      subtitle: "Subscriptions can be cancelled anytime for any reason",
    },
  ],
  primaryGoalQuestion: "What's the most important thing you want your time for?",
  timeLostQuestion: "Where do you lose the most time?",
  timeLostSubtitle: "You chose {goal} — where does the time actually go?",
  difficultyQuestion: "How hard is it to resist?",
  goals: {
    work: "Work",
    study: "Study",
    sleep: "Sleep",
    family: "Family",
    hobby: "Hobby",
    exercise: "Exercise",
    balance: "Balance",
    wellness: "Wellness",
    other: "Other",
  },
  timeLostOptions: {
    social: "Social",
    news: "News",
    gaming: "Gaming",
    streaming: "Streaming",
    work: "Work",
    shopping: "Shopping",
    messages: "Messages",
    browsing: "Browsing",
    other: "Other",
  },
  difficultyEmojis: ["😌", "🙂", "😐", "😣", "😫"],
  difficultyLevels: [
    "Very easy",
    "Easy",
    "Sometimes hard",
    "Hard",
    "Very hard",
  ],
  insight8ImageAlt: "Evening scene with phone set aside",
  insight8Headline: "**This matters.**",
  insight8Body:
    "Protecting your time is one of the boundaries that actually works. Small limits add up to real change.",
  insight8SleepHeadline: "**This matters.**",
  insight8SleepBody:
    "After 7 p.m. the phone can wait. **Unplugging in the evening** is one of the boundaries that work.",
  insight9ImageAlt: "Illustration about attention and screens",
  insight9SocialHeadline: "You're up against a **machine**.",
  insight9SocialBody: "The feed has no end — it's **designed** that way.",
  insight9OtherHeadline: "Every notification **breaks** your focus.",
  insight9OtherBody: "Getting back can take **20 minutes**.",
  notificationsHeadline: "We'll send a reminder **2 days** before your trial ends",
  notificationsBody:
    "Allow notifications so we can send you a reminder before being charged",
  notificationsAllow: "ALLOW NOTIFICATIONS",
  notificationsMaybeLater: "MAYBE LATER",
  notificationsDeniedWarning:
    "Notifications are off. Turn them on in Settings so we can remind you before your trial ends.",
  setupImageAlt: "Permission setup illustration",
  setupTitle: "One quick permission",
  setupBody:
    "Screen0 needs system access to show your personal usage report. No account required.",
  setupNativeHint: "You can change this anytime in Settings.",
  setupWebHint: "On the web, usage reports need the mobile app. You can continue and set this up later on your phone.",
  overlayImageAlt: "Display-over-apps permission illustration",
  overlayTitle: "Allow the block screen",
  overlayBody:
    "Screen0 needs “Display over other apps” so a block screen can appear when a locked app opens. Without it, rules cannot protect you.",
  overlayNativeHint:
    "You’ll open Android settings. Enable Screen0, then return to the app.",
  overlayCta: "OPEN SETTINGS",
  overlayNotNow: "NOT NOW",
  vpnImageAlt: "On-device website filter illustration",
  vpnTitle: "Install the website filter",
  vpnBody:
    "To block websites in Safari and other mobile browsers, Screen0 needs to add a local filter profile.",
  vpnBodyAndroid:
    "To block websites in Chrome and other mobile browsers, Screen0 needs to add a local filter profile.",
  vpnPrivacyTitle: "Commitment to privacy",
  vpnPrivacyBody:
    "This adds an on-device filter that blocks only the websites you choose. All filtering happens on your phone — your data never leaves the device.",
  vpnWebHint: "On the web, website blocking needs the mobile app. You can continue and set this up later on your phone.",
  vpnInstall: "INSTALL PROFILE",
  vpnNotNow: "NOT NOW",
  vpnErrorTitle: "Couldn't install the filter",
  vpnErrorHint: "The VPN profile wasn't added. You can skip for now and try again later.",
  trackingImageAlt: "Anonymous analytics illustration",
  trackingTitle: "Support a better Screen0",
  trackingBody:
    "Allowing anonymous tracking helps us understand what's working and improve the app for everyone.",
  trackingPrivacyTitle: "Commitment to privacy",
  trackingPrivacyBody: "No ads, no personal data, no selling your data. Ever.",
  trackingAllow: "CONTINUE",
  trackingNotNow: "NOT NOW",
  welcomeImageAlt: "Welcome illustration",
  welcomeTitle: "You're ready.",
  welcomeRecap: "You want more time for **{goal}**. Screen0 will help you protect it — starting today.",
  welcomeRecapFallback: "Screen0 will help you protect your time — starting today.",
  paywall: {
    headline: "Gain 2+ Hours Daily with Screen0 Premium",
    timeline: [
      {
        title: "Begin your focus journey",
        description:
          "You're well on your way to turning intention into a daily focus habit.",
      },
      {
        title: "Today: Take back your focus",
        description:
          "Unlock powerful tools like **Rules** and **Smart Blocks**. Set up your first limit and protect your time today.",
      },
      {
        title: "Day 5: Feel the difference",
        description:
          "By now, you'll notice more calm, clarity, and control over your time. We'll remind you before your trial ends—no surprises.",
      },
      {
        title: "Day 7: Keep it going",
        description:
          "Continue your focus journey with Premium. Cancel anytime if it's not for you.",
      },
    ],
    timelineTrialDescriptionWithTrial:
      "Start your {trialDays}-day free trial and unlock powerful tools like **Rules** and **Smart Blocks**. Set up your first limit and protect your time today.",
    timelineTrialDescriptionNoTrial:
      "Unlock powerful tools like **Rules** and **Smart Blocks**. Set up your first limit and protect your time today.",
    yearlyBadgeWithTrial: "{trialDays} day free trial, then {price} /year",
    yearlyBadgeNoTrial: "{price} /year",
    yearlyTitle: "Yearly",
    yearlySubtext: "(Only {monthlyPrice} /mo — less than a cup of coffee)",
    lifetimeTitle: "Lifetime",
    lifetimeSubtext: "One-time purchase. Yours forever.",
    ctaYearly: "START FREE TRIAL",
    ctaLifetime: "GET LIFETIME ACCESS",
    footerRenewal: "Auto-renews until cancelled. Cancel anytime.",
    footerLifetime: "One-time purchase. No subscription.",
    termsLink: "Terms",
    privacyLink: "Privacy",
    purchase: {
      processing: "Processing…",
      genericError: "Something went wrong. Please try again.",
      notEntitled: "Purchase completed, but Premium isn't active yet. Please try again.",
      webUnavailable: "Purchases are available only in the Screen0 app.",
      configError: "Purchases aren't available right now. Please try again later.",
      offeringsError: "Couldn't load subscription prices. Check your connection and try again.",
      retry: "Retry",
      restorePurchases: "Restore Purchases",
      restoreProcessing: "Restoring…",
      restoreSuccess: "Purchases restored. Premium is active.",
      restoreNothingFound: "No purchases found to restore.",
      restoreNothingFoundApple: "No purchases found for this Apple ID.",
      restoreNothingFoundGoogle: "No purchases found for this Google Play account.",
      restoreError: "Couldn't restore purchases. Please try again.",
    },
  },
} as const;

export const EN_CALENDAR_COPY = {
  chooseDate: "Choose date",
  previousMonth: "Previous month",
  nextMonth: "Next month",
} as const;

export const EN_QUERY_COPY = {
  loadingDefault: "Loading…",
  errorTitle: "Something went wrong",
} as const;

export const EN_APP_COPY = {
  syncError:
    "Could not save today's usage. Screen0 will retry when you reopen the app.",
  reinstallRulesPurged:
    "Your blocking rules were reset because app selections don't carry over after reinstalling. Set them up again in Rules.",
} as const;

export const EN_FORMAT_COPY = {
  activationSingular: "Activation",
  activationPlural: "Activations",
  durationMinutes: "{minutes}m",
  durationHoursOnly: "{hours}h",
  durationOneHour: "1h",
  durationHoursMinutes: "{hours}h {minutes}m",
  allDay: "All day",
  timeBandMorning: "morning",
  timeBandAfternoon: "afternoon",
  timeBandEvening: "evening",
  timeBandNight: "night",
} as const;

export const EN_APP_DETAIL_COPY = {
  dailyAverage: "Daily average",
  dailyAverageInfoAria: "About {metric} daily average",
  dailyAverageInfoBodyTime1:
    "Average hourly screen time for this app over the last ~12 months.",
  dailyAverageInfoBodyTime2:
    "Each hour is averaged across all days with device data in that period. Days without usage for this app count as zero. Days before data collection started are excluded.",
  dailyAverageInfoBodyOpens1:
    "Average hourly activations for this app over the last ~12 months.",
  dailyAverageInfoBodyOpens2:
    "Each hour is averaged across all days with device data in that period. Days without usage for this app count as zero. Days before data collection started are excluded.",
  panelCalendar: "Calendar",
  panelChart: "Chart",
  noUsage: "No usage",
  activationsNotTrackedPeriod: "Activations not tracked for this period.",
} as const;

export const EN_RING_COPY = {
  screenTime: "Screen time",
  activations: "Activations",
  percentOfTotal: "{percent}% of total",
  notTracked: "Not tracked",
  screenActivationsByApp: "Screen activations by app",
  screenTimeByApp: "Screen time by app",
  showScreenTime: "Show screen time",
  showScreenActivations: "Show screen activations",
  chartDescriptionDay:
    "Hourly screen time and activations for the selected day",
  chartDescriptionWeek:
    "Daily screen time and activations for the selected week",
  chartDescriptionMonth:
    "Daily screen time and activations for the selected month",
  activationsCount: "{count} activations",
  activationsNotTrackedDay: "Activations not tracked for this day.",
  activationsNotTrackedWeek: "Activations not tracked for this week.",
  activationsNotTrackedMonth: "Activations not tracked for this month.",
  usageDetailAria: "{name} usage detail",
  tooltipActAbbrev: " · {count} act",
} as const;

export const EN_SMART_BLOCK_COPY = {
  goalDoom: "Reduce doom-scroll",
  goalCompulsivity: "Reduce compulsivity",
  actionLabel: "Block completely {range}",
  reasonDoomShare:
    "{sharePct}% of your {name} use happens between {range}. Blocking that window targets your highest-risk period.",
  reasonDoomCluster:
    "{name} use tends to cluster in this part of the day. Blocking {range} targets your highest-risk period.",
  reasonCompulsivityOpens: "You open {name} most often between {range}.",
  reasonCompulsivityTime: "Most of your {name} time lands between {range}.",
  reasonCompulsivityShareSuffix:
    "A full block in that window breaks the habit loop.",
  reasonCompulsivityRepeat:
    "{name} gets repeated pickups through this part of the day. A full block between {range} breaks the habit loop.",
  reactivateHint: "Rule exists but is off — tap to turn it back on",
} as const;

export const EN_PERMISSION_STATUS_COPY = {
  granted: "Granted",
  denied: "Denied",
  unavailable: "Unavailable",
  notDetermined: "Not determined",
} as const;

export const EN_CATEGORY_COPY = {
  books: "Books",
  business: "Business",
  catalogs: "Catalogs",
  developerTools: "Developer Tools",
  education: "Education",
  entertainment: "Entertainment",
  finance: "Finance",
  foodAndDrink: "Food & Drink",
  games: "Games",
  graphicsAndDesign: "Creativity",
  healthAndFitness: "Health & Fitness",
  kids: "Kids",
  lifestyle: "Lifestyle",
  medical: "Medical",
  miscellaneous: "Miscellaneous",
  music: "Music",
  navigation: "Navigation",
  news: "Information & Reading",
  newsstand: "Newsstand",
  photoAndVideo: "Photo & Video",
  productivity: "Productivity & Finance",
  reference: "Reference",
  shopping: "Shopping & Food",
  socialNetworking: "Social",
  sports: "Sports",
  stickers: "Stickers",
  travel: "Travel",
  system: "System",
  utilities: "Utilities",
  weather: "Weather",
  uncategorized: "Other",
  other: "Other",
  website: "Website",
} as const;

export const EN_TRANSLATIONS = {
  common: EN_COMMON_COPY,
  navigation: EN_NAVIGATION_COPY,
  today: EN_TODAY_COPY,
  habits: EN_HABITS_COPY,
  insight: EN_INSIGHT_COPY,
  rules: EN_RULES_COPY,
  chat: EN_CHAT_COPY,
  insightChat: EN_INSIGHT_CHAT_COPY,
  settings: EN_SETTINGS_COPY,
  language: EN_LANGUAGE_COPY,
  permission: EN_PERMISSION_COPY,
  onboarding: EN_ONBOARDING_COPY,
  calendar: EN_CALENDAR_COPY,
  query: EN_QUERY_COPY,
  app: EN_APP_COPY,
  format: EN_FORMAT_COPY,
  appDetail: EN_APP_DETAIL_COPY,
  ring: EN_RING_COPY,
  smartBlock: EN_SMART_BLOCK_COPY,
  permissionStatus: EN_PERMISSION_STATUS_COPY,
  categories: EN_CATEGORY_COPY,
  websiteCategories: EN_WEBSITE_CATEGORIES_COPY,
} as const;

export type EnTranslations = typeof EN_TRANSLATIONS;
export default EN_TRANSLATIONS;
